/** Access живёт в памяти вкладки. Соседние вкладки делятся им, чтобы не гонять refresh. */

type AccessReader = () => string | null
type AccessListener = (token: string) => void

const CHANNEL_NAME = 'okidoki-auth'
const PEER_WAIT_MS = 100

let readAccess: AccessReader = () => null
let onAccess: AccessListener = () => {}
let channel: BroadcastChannel | null = null

function ensureChannel(): BroadcastChannel | null {
    if (channel) {
        return channel
    }
    if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
        return null
    }
    channel = new BroadcastChannel(CHANNEL_NAME)
    channel.onmessage = (event: MessageEvent) => {
        const data = event.data
        if (!data || typeof data !== 'object') {
            return
        }
        if (data.type === 'access' && typeof data.token === 'string' && data.token) {
            onAccess(data.token)
            return
        }
        if (data.type === 'request') {
            const token = readAccess()
            if (token) {
                channel?.postMessage({ type: 'access', token })
            }
        }
    }
    return channel
}

function decodeJwtPayload(token: string): { iat?: number } | null {
    try {
        const part = token.split('.')[1]
        if (!part) {
            return null
        }
        const base64 = part.replace(/-/g, '+').replace(/_/g, '/')
        const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
        return JSON.parse(atob(padded))
    } catch {
        return null
    }
}

export function accessIssuedAt(token: string | null): number {
    if (!token) {
        return 0
    }
    return Number(decodeJwtPayload(token)?.iat) || 0
}

export function installAuthTabSync(read: AccessReader, adopt: AccessListener): void {
    readAccess = read
    onAccess = adopt
    if (typeof window !== 'undefined') {
        ensureChannel()
    }
}

export function publishAccessToken(token: string): void {
    if (!token) {
        return
    }
    ensureChannel()?.postMessage({ type: 'access', token })
}

let peerRequest: Promise<string | null> | null = null

export function requestPeerAccessToken(timeoutMs = PEER_WAIT_MS): Promise<string | null> {
    if (peerRequest) {
        return peerRequest
    }
    const current = ensureChannel()
    if (!current) {
        return Promise.resolve(null)
    }
    const request = new Promise<string | null>((resolve) => {
        let settled = false
        const finish = (token: string | null) => {
            if (settled) {
                return
            }
            settled = true
            current.removeEventListener('message', onMessage)
            clearTimeout(timer)
            resolve(token)
        }
        const onMessage = (event: MessageEvent) => {
            const data = event.data
            if (data?.type === 'access' && typeof data.token === 'string' && data.token) {
                finish(data.token)
            }
        }
        const timer = setTimeout(() => finish(null), timeoutMs)
        current.addEventListener('message', onMessage)
        current.postMessage({ type: 'request' })
    })
    peerRequest = request
    void request.finally(() => {
        if (peerRequest === request) {
            peerRequest = null
        }
    })
    return request
}

export function resetAuthTabSyncForTests(): void {
    channel?.close()
    channel = null
}
